import { mapGetters, mapActions, mapMutations } from 'vuex';

import ContentType from '@/helpers/contentType';
import CategoryType from '@/helpers/contentCategory';
import AcademicMainType from '@/helpers/academicMainType';
import TemplateType from '@/helpers/templateType';
import { hasPermissions } from '~/helpers/permission';
import { PROCESSING_STATUS } from '@/helpers/avaliaActivity';

import commonMixins from '@/mixins/templates/common';
import courseMixins from '@/mixins/templates/course';
import contentSync from '@/mixins/content-sync';

import { ABtn } from '@maisaedu/ui-components-a-btn';
import genericModal from '@/components/ui/generic-modal';
import modalMoveTo from '@/components/content-v2/modals/move-to';
import ChooseCorrectionTypeModal from '@/components/content-v2/modals/choose-correction';

export default {
  name  : "TopicSettingsMenu",
  props : {
    topic: {
      type    : Object,
      default : {}
    },
    menuType: {
      type    : String,
      default : false
    },
    template: {
      type    : Number,
      default : TemplateType.STREAMING
    },
    index: {
      type    : Number,
      default : null
    },
    disabled: {
      type    : Boolean,
      default : false
    },
    menuProps: {
      type    : Object,
      default : () => ({})
    },
    isMenuActivated: {
      type    : Boolean,
      defualt : false
    }
  },
  mixins     : [commonMixins, courseMixins, contentSync],
  components : { genericModal, modalMoveTo, ABtn, ChooseCorrectionTypeModal },
  data() {
    return {
      TemplateType,
      isTopicEditingConditionals       : false,
      chooseCorrectionTypeModalOptions : {
        isOpen                       : false,
        questionCollectionExternalId : ''
      },
      isMenuOpen       : false,
      moveModalOptions : {
        isOpen    : false,
        isLoading : false
      },
      addPage: {
        templates           : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile        : true,
        text                : 'content.elementOptions.newModule',
        icon                : 'mdi-post-outline',
        permissionTopicType : ContentType.PAGE,
        func                : () => this.createModule(this.topic),
        permissionAlias     : 'add'
      },
      addContent: {
        templates         : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile      : true,
        text              : 'content.elementOptions.newContentOrActivity',
        icon              : 'mdi-toy-brick-plus-outline',
        func              : () => this.emitCreateContentOrExercise(this.topic),
        permissionAliases : ContentType.getPermissions('add')
      },
      copyContent: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.copy',
        icon            : 'mdi-file-move-outline',
        func            : () => this.handleCopyContent(this.topic),
        permissionAlias : 'copy'
      },
      renameTitle: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.rename',
        icon            : 'mdi-pencil-outline',
        func            : () => this.changeIsEditingTopic(this.index),
        permissionAlias : 'rename'
      },
      conditionalAvailability: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'conditionalAvailability.availability',
        icon            : 'mdi-lock',
        func            : () => this.editConditionalAvailability(this.topic),
        permissionAlias : 'add'
      },
      deleteTopic: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.delete',
        icon            : 'mdi-trash-can-outline',
        func            : () => (this.managementModalDeleteTopic(this.topic)),
        permissionAlias : 'delete'
      },
      menuDivider: {
        templates    : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile : true,
        divider      : true
      },
      configPage: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'common.edit',
        icon            : 'mdi-cog-outline',
        func            : () => this.editTopic(this.topic),
        permissionAlias : 'configure'
      },
      configModule: {
        templates       : [TemplateType.STREAMING],
        isOnlyMobile    : true,
        text            : 'common.edit',
        icon            : 'mdi-cog-outline',
        func            : () => (this.$nuxt.$emit('openModuleGroupConfigurationModal', this.topic.id)),
        permissionAlias : 'configure'
      },
      editPage: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.edit',
        icon            : 'mdi-pencil-outline',
        func            : () => this.openTopic(this.topic),
        permissionAlias : 'configure'
      },
      editTopicContent: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.edit',
        icon            : 'mdi-pencil-outline',
        func            : () => this.editTopic(this.topic),
        permissionAlias : 'edit'
      },
      moveTopic: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.moveTo',
        icon            : 'mdi-folder-move-outline',
        func            : () => this.openMoveModal(this.topic),
        permissionAlias : 'configure'
      },
      viewResults: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.results',
        icon            : 'mdi-finance',
        func            : () => this.viewAvaliaActivityResults(this.topic),
        permissionAlias : 'result'
      },
      avaliaActivityCorrection: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        removeTopicId   : true,
        isOnlyMobile    : false,
        text            : 'content.topicOptions.correction',
        icon            : 'mdi-clipboard-check-outline',
        func            : () => this.openAvaliaChooseCorrectionModal(this.topic),
        permissionAlias : 'evaluations_center'
      },
      avaliaDashboard: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.dashboard',
        icon            : 'mdi-chart-box-outline',
        func            : () => this.openAvaliaDashboard(this.topic),
        permissionAlias : 'dashboard'
      },
      avaliaClearAnswers: {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : 'content.topicOptions.clearAnswers',
        icon            : 'mdi-broom',
        func            : () => this.clearAnswers(this.topic),
        permissionAlias : 'delete'
      },
      associatedCount: 0
    };
  },
  watch: {
    isMenuOpen(value) {
      this.$emit('update:isMenuActivated', value);
    }
  },
  computed: {
    ...mapGetters({
      getterCourse          : 'plataforma/newContent/getterCourse',
      getterContent         : 'plataforma/newContent/getterContent',
      getterUserRoleContext : 'plataforma/user/getterUserContext'
    }),
    localMenuProps() {
      return ({
        offsetY     : true,
        left        : true,
        nudgeBottom : '8px',
        ...this.menuProps
      });
    },
    getContext() {
      return this.$route.params.id ? this.getterContent.context : this.getterCourse.context;
    },
    getTopics() {
      return this.$route.params.id ? this.getterContent.topics : this.getterCourse.topics;
    },
    getPermissions() {
      return this.$route.params.id ? this.getterContent.permissions : this.getterCourse.permissions;
    },
    // Needs to be here because icon and text is reactive
    hideTopic() {
      return {
        templates       : [TemplateType.TIMELINE, TemplateType.STREAMING],
        isOnlyMobile    : false,
        text            : this.topic.isVisible ? 'content.topicOptions.hide' : 'content.topicOptions.show',
        icon            : this.topic.isVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline',
        func            : () => this.hideAndShowTopic(this.topic),
        permissionAlias : 'hide'
      };
    },
    filteredTopicsToMove() {
      if (this.topic) {
        if (!this.topic.parentTopicId) {
          const topicsToMove = this.getTopics.filter(topic => topic.topicTypeId === ContentType.FOLDER);

          return topicsToMove;
        } else {
          const topicsToMove = this.getTopics.filter(
            (topic) =>
              topic.topicTypeId === ContentType.FOLDER &&
              this.topic.parentTopicId !== topic.id
          );

          return topicsToMove;
        }
      }
    },
    menuOptions() {
      let options = [];

      switch (this.topic.topicTypeId) {
        case ContentType.FOLDER:
          options =  [
            this.addPage,
            this.addContent,
            this.menuDivider,
            this.conditionalAvailability,
            this.hideTopic,
            this.deleteTopic
          ];

          if (this.menuType === 'add-config' || this.template === TemplateType.TIMELINE) {
            options.splice(2, 0, this.renameTitle);
          }

          if (this.menuType === 'add-config' && (this.topic.children && this.topic.children.length) ) {
            options.splice(3, 0, this.configModule);
          }
          break;
        case ContentType.PAGE:
          options =  [
            this.configPage,
            this.editPage,
            this.conditionalAvailability,
            this.moveTopic,
            this.hideTopic,
            this.deleteTopic
          ];
          break;
        default:
          options =  [
            this.editTopicContent,
            this.conditionalAvailability,
            this.hideTopic,
            (this.topic.topicTypeId === ContentType.AVALIA && this.topic.wasAnswered) ? { ...this.deleteTopic, disabled: true } : this.deleteTopic
          ];

          if (this.shouldShowResults(this.topic)) {
            options.splice(1, 0, this.viewResults);
          }

          if (this.topic.topicTypeId === ContentType.AVALIA && this.topic.content?.isActive && this.topic.content.status === PROCESSING_STATUS.OK) {
            options.splice(1, 0, this.avaliaActivityCorrection);

            if (this.getContext.academicMainTypeId !== AcademicMainType.MODEL && this.getterUserRoleContext.isAdministrator) {
              if (this.topic.wasAnswered) {
                options.splice(options.length - 1, 0, this.avaliaClearAnswers);
              } else {
                options.splice(options.length - 1, 0, { ...this.avaliaClearAnswers, disabled: true });
              }
            }
          }

          if (this.shouldShowDashboard(this.topic)) {
            options.splice(1, 0, this.avaliaDashboard);
          }

          if (this.getContext.topicTypeId !== ContentType.PAGE) {
            options.splice(2, 0, this.moveTopic);
          }

          if (this.topic.categoryTypeId === CategoryType.HTML) {
            options.splice(3, 0, this.copyContent);
          }

          break;
      }

      switch (this.menuType) {
        case 'only-config':
          return options.filter(opt => !opt.isOnlyMobile && opt.templates?.includes(this.template));
        case 'only-add':
          return options.filter(opt => opt.isOnlyMobile && opt.templates?.includes(this.template));
        case 'add-config':
          return options.filter(opt => opt.templates?.includes(this.template));
        default:
          return [];
      }
    },
    menuOptionsFiltered() {
      return this.menuOptions.filter((menuItem) => {
        const permissionAliases = menuItem.permissionAliases || [];

        if (menuItem.permissionAlias) {
          permissionAliases.push(menuItem.permissionAlias);
        }

        return menuItem.divider || hasPermissions(
          this.getContext.academicMainId,
          this.getContext.academicMainTypeId,
          this.topic.isMarketplace,
          menuItem.removeTopicId ? null : (menuItem.permissionTopicType || this.topic.topicTypeId),
          permissionAliases,
          this.getPermissions
        );
      });
    },
    showMenu() {
      return !!this.menuOptionsFiltered.filter((menuItem) => !menuItem.divider).length;
    },
    isApprovalFlowAndMenuTypeAdd() {
      if (this.menuType === "only-add" && this.isMaxModuleApprovalFlow) {
        return this.countAllModules < this.getterCourse.context.maxModule;
      }

      return true;
    }
  },
  methods: {
    ...mapActions({
      actionPatchMoveToTopic : 'plataforma/newContent/actionPatchMoveToTopic',
      actionDeleteTopic      : 'plataforma/newContent/actionDeleteTopic',
      actionClearAnswers     : 'plataforma/newContent/actionClearAnswers'
    }),
    ...mapMutations({
      SET_IS_EDITING_TOPIC: 'plataforma/newContent/SET_IS_EDITING_TOPIC'
    }),
    clearAnswers(topic) {
      this.$dialog.show({
        type      : 'warning',
        title     : this.$t('content.modalClearAnswers.title'),
        message   : this.$t('content.modalClearAnswers.message'),
        target    : 'clear-answers-dialog',
        onConfirm : async ({ startLoading, stopLoading, done }) => {
          startLoading();

          const params = {
            academicMainId : this.getContext.academicMainId,
            topicId        : topic.id
          };

          this.actionClearAnswers(params)
            .then(() => {
              this.$toast.success(this.$t('content.modalClearAnswers.answersCleanupCompleted'));
            }).catch((err) => {
              this.$toast.error(err.data.error.message);
            }).finally(() => {
              stopLoading();
              done();
            });
        }
      });
    },
    async managementModalDeleteTopic(topic) {
      if (this.isMasterCourse) {
        const { data } = await this.$services.contentV2().getAssociatedTopicProgress({
          topicId        : topic.id, 
          academicMainId : this.$route.params.courseId 
        });
        
        topic.wasAnswered = !!data.wasAnswered;
        topic.wasMarkedProgress = !!data.wasMarkedProgress;
        topic.hasGrade = !!data.hasGrade;
        this.associatedCount = data.associatedCount || 0;
      }
      
      this.$dialog.show({
        type              : 'delete',
        title             : this.$t('content.modalDeleteTopic.title'),
        confirmButtonText : this.$t('content.modalDeleteTopic.textPrimaryButton'),
        message           : this.mountDeleteText(topic),
        target            : 'delete-topic-dialog',
        onConfirm         : async ({ startLoading, stopLoading, done }) => {
          startLoading();

          const params = {
            topic,
            academicMainId : this.$route.params.courseId,
            version        : this.getContext.version
          };

          return this.actionDeleteTopic(params)
            .then(() => {
              this.$toast.success(this.$t('content.successMessagesSnackbar.deleteTopic'));
            }).catch((err) => {
              this.$toast.error(err.data.error.message);
            }).finally(() => {
              stopLoading();
              done();
            });
        }
      });
    },
    mountDeleteText(topic) {
      let message = `${this.$t('content.modalDeleteTopic.description')} <b>${topic.title}</b>`;

      const relatedItems = [];

      if ((topic.topicTypeId === ContentType.FOLDER || topic.topicTypeId === ContentType.PAGE) && topic.children && topic.children.length) {
        relatedItems.push(this.$t('content.modalDeleteTopic.childObjects'));

        if (
          topic.children.some(child => child.hasGrade) ||
          this.isMasterCourse && topic.hasGrade
        ) {
          relatedItems.push(this.$t('content.modalDeleteTopic.evaluativeObjects'));
        }

        if (
          topic.children.some(topic => topic.wasAnswered) || 
          this.isMasterCourse && topic.wasAnswered
        ) {
          relatedItems.push(this.$t('content.modalDeleteTopic.activitiesDelivery'));
        }

        if (topic.children.some(topic => topic.isRecordProgress && topic.wasMarkedProgress)) {
          relatedItems.push(this.$t('content.modalDeleteTopic.progressOnObjects'));
        }
      } else {
        if (topic.hasGrade) {
          relatedItems.push(this.$t('content.modalDeleteTopic.evaluativeObjects'));
        }

        if (topic.wasAnswered) {
          relatedItems.push(this.$t('content.modalDeleteTopic.activitiesDelivery'));
        }

        if (
          topic.isRecordProgress && topic.wasMarkedProgress ||
          this.isMasterCourse && topic.wasMarkedProgress
        ) {
          relatedItems.push(this.$t('content.modalDeleteTopic.progressOnObjects'));
        }

        if (topic.topicTypeId === ContentType.AVALIA) {
          relatedItems.push(this.$t('content.modalDeleteTopic.allLinksWithClassDiscipline'));
          relatedItems.push(this.$t('content.modalDeleteTopic.allQuestions'));
        }
      }

      if (relatedItems.length) {
        message += this.isMasterCourse && this.associatedCount
          ? ' ' + this.$tc('content.modalDeleteTopic.objectsRelatedInMasterCourse', this.associatedCount !== 1, { associatedQty: this.associatedCount })
          : this.$t('content.modalDeleteTopic.objectsRelated');
        message += '<br>';
        message += `<b>${ this.$t('content.modalDeleteTopic.actionNotUndone') }</b>`;
        message += '<br>';
        message += `<b class="d-block mt-6 mb-2">${ this.$t('content.modalDeleteTopic.relatedItemssCanBeDeleted') }</b>`;

        message += '<ul>';

        for (let index = 0; index < relatedItems.length; index++) {
          message += `<li>${relatedItems[index]}</li>`;
        }

        message += '</ul>';

        message += this.isMasterCourse && this.associatedCount 
          ? `<br><span>${this.$t('content.modalDeleteTopic.masterTopicDeleteResult')}</span>`
          : '';
      } else {
        message += '<br>';
        message += `<b>${ this.$t('content.modalDeleteTopic.actionNotUndone') }</b>`;
       
        message += this.isMasterCourse && this.associatedCount 
          ? `<br><div class="mt-4">${this.$t('content.modalDeleteTopic.masterTopicDeleteResult')}</div>`
          : '';
      }

      return message;
    },
    editConditionalAvailability(topic) {
      this.isTopicEditingConditionals = true;
      this.$nuxt.$emit('changeIsOpenConditionalAvailabilityModal', {
        isVisibleIsControlledByConditional : topic.isVisibleIsControlledByConditional,
        conditionals                       : topic.conditionals,
        hasToCallSaveAction                : true,
        topic
      });
    },
    createModule(topic = false) {
      const params = {
        parentTopicId      : topic ? topic.id : null,
        academicMainTypeId : this.getContext.academicMainTypeId,
        version            : this.getContext.version,
        academicMainId     : this.getContext.academicMainId
      };

      this.$nuxt.$emit('changeIsOpenModalContentPage', params);
    },
    handleCopyContent(topic) {
      this.$nuxt.$emit('changeIsOpenModalCopyContentTo', topic);
    },
    emitCreateContentOrExercise(topic) {
      const params = {
        academicMainId     : this.getContext.academicMainId,
        academicMainTypeId : this.getContext.academicMainTypeId,
        parentTopic        : topic,
        version            : this.getContext.version,
        isMarketplace      : false
      };

      this.$nuxt.$emit('changeIsOpenModalContentTypes', params);
    },
    changeIsEditingTopic(index) {
      this.SET_IS_EDITING_TOPIC(index);
    },
    editTopic(topic) {
      const params = {
        academicMainId            : this.getContext.academicMainId,
        academicMainTypeId        : this.getContext.academicMainTypeId,
        topicId                   : topic.id,
        version                   : this.getContext.version,
        isParentRecordingProgress : this.getContext.isRecordProgress || null,
        isEditing                 : true
      };

      const openEditModalEvent = CategoryType.getEditModalEventById(topic.categoryTypeId);
      this.$nuxt.$emit(openEditModalEvent, params);
    },
    openMoveModal() {
      this.moveModalOptions.isOpen = !this.moveModalOptions.isOpen;
    },
    moveTo(newParentTopic) {
      const params = {
        academicMainId   : this.$route.params.courseId,
        newParentTopicId : newParentTopic && newParentTopic.id ? newParentTopic.id : null,
        topic            : this.topic,
        version          : this.getContext.version
      };

      this.moveModalOptions.isLoading = true;

      this.actionPatchMoveToTopic(params)
        .then(() => {
          this.moveModalOptions.isOpen = false;

          this.$toast.success(this.$t('content.modalMoveTo.itemMovedSuccessfully'));
        }).catch((err) => {
          this.$toast.error(err.data.error.message);
        }).finally(() => {
          this.moveModalOptions.isLoading = false;
        });
    },
    shouldShowResults(topic) {
      if (topic.topicTypeId !== ContentType.AVALIA) {
        return false;
      }
      if (!this.topic.content?.isActive || this.topic.content.status !== PROCESSING_STATUS.OK) {
        return false;
      }

      if (this.getterUserRoleContext.isStudent) {
        return !topic.isBeforeDeadline;
      } else {
        return !topic.isFuture;
      }
    },
    shouldShowDashboard(topic) {
      return topic.topicTypeId === ContentType.AVALIA && !this.getterUserRoleContext.isStudent && this.topic.content?.isActive && this.topic.content.status === PROCESSING_STATUS.OK;
    },
    viewAvaliaActivityResults(topic) {
      const queryParams = {
        destinationPage                  : 'results',
        classExternalId                  : this.getContext.academicMainExternalId,
        activityExternalSource           : topic.content.activityExternalSource,
        questionCollectionExternalSource : topic.content.questionCollectionExternalSource,
        activityExternalId               : topic.content.activityExternalId,
        questionCollectionExternalId     : topic.content.questionCollectionExternalId
      };

      this.callAvaliaSSO(queryParams, topic);
    },
    openAvaliaChooseCorrectionModal(topic) {
      this.chooseCorrectionTypeModalOptions = {
        isOpen                       : true,
        questionCollectionExternalId : topic.content.questionCollectionExternalId
      };
    },
    openAvaliaDashboard(topic) {
      const queryParams = {
        destinationPage : 'dashboard',
        externalId      : topic.content.questionCollectionExternalId,
        routeQuery      : JSON.stringify(this.$route.query)
      };

      this.callAvaliaSSO(queryParams, topic);
    }
  }
};
